import { apiFetch } from '../../shared/apiClient';
import type {
  CustomFieldCatalogEntry,
  CustomFieldKind,
  CustomFieldPreviewResponse,
  ProductCustomField,
  ProductCustomFieldUpdate,
} from '../../features/customFields/types';
import type { JiraField } from '../../shared/types';

async function unwrap<T>(res: Response, fallbackError: string): Promise<T> {
  if (!res.ok) {
    let detail: string | undefined;
    try {
      const body = (await res.json()) as { error?: string };
      detail = body && typeof body.error === 'string' ? body.error : undefined;
    } catch {
      /* non-json */
    }
    throw new Error(detail || fallbackError);
  }
  return (await res.json()) as T;
}

/**
 * Custom Fields API client.
 *
 * Catalog endpoints are org-scoped; product endpoints require an active
 * product (X-Product-Id header is added by `apiFetch` automatically).
 */
const CustomFieldsService = {
  async getCatalog(): Promise<CustomFieldCatalogEntry[]> {
    const data = await unwrap<{ fields: CustomFieldCatalogEntry[] }>(
      await apiFetch('/api/custom-fields/catalog'),
      'Failed to fetch custom fields catalog'
    );
    return data.fields || [];
  },

  async addCatalogEntry(payload: {
    jiraFieldId: string;
    displayName: string;
    fieldKind: CustomFieldKind;
  }): Promise<CustomFieldCatalogEntry> {
    return unwrap<CustomFieldCatalogEntry>(
      await apiFetch('/api/custom-fields/catalog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
      'Failed to register custom field'
    );
  },

  async removeCatalogEntry(id: number): Promise<void> {
    await unwrap<{ success: boolean }>(
      await apiFetch(`/api/custom-fields/catalog/${id}`, { method: 'DELETE' }),
      'Failed to remove custom field'
    );
  },

  async getProductSettings(): Promise<ProductCustomField[]> {
    const data = await unwrap<{ fields: ProductCustomField[] }>(
      await apiFetch('/api/custom-fields/product'),
      'Failed to fetch product custom fields'
    );
    return data.fields || [];
  },

  async updateProductSetting(
    orgFieldId: number,
    payload: ProductCustomFieldUpdate
  ): Promise<ProductCustomField> {
    return unwrap<ProductCustomField>(
      await apiFetch(`/api/custom-fields/product/${orgFieldId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
      'Failed to update custom field activation'
    );
  },

  /**
   * Copy the supplied toggles for one catalog field to every product in the
   * org. The server iterates products inside a transaction and invalidates
   * downstream caches per product.
   */
  async applyToAllProducts(
    orgFieldId: number,
    payload: {
      inFilters: boolean;
      inTable: boolean;
      inSummary: boolean;
      inDistribution: boolean;
    }
  ): Promise<{ appliedTo: number; productIds: string[] }> {
    return unwrap<{ appliedTo: number; productIds: string[] }>(
      await apiFetch(`/api/custom-fields/product/${orgFieldId}/apply-to-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
      'Failed to apply to all products'
    );
  },

  /**
   * Read-only preview for the "Add custom field" form. Fetches up to 3 sample
   * values from Jira for the given custom field and returns a recommended
   * `field_kind`. Always succeeds (server falls back gracefully on errors).
   */
  async getFieldPreview(
    jiraFieldId: string,
    signal?: AbortSignal
  ): Promise<CustomFieldPreviewResponse> {
    const path = `/api/custom-fields/preview?jiraFieldId=${encodeURIComponent(jiraFieldId)}`;
    return unwrap<CustomFieldPreviewResponse>(
      await apiFetch(path, { signal }),
      'Failed to preview custom field'
    );
  },

  async getJiraFields(): Promise<JiraField[]> {
    const res = await apiFetch('/api/jira/fields');
    if (!res.ok) {
      throw new Error('Failed to fetch Jira fields');
    }
    const data = await res.json();
    return Array.isArray(data) ? data : data.fields || [];
  },

  /**
   * Streams SSE progress; resolves with the final summary when done.
   *
   * The optional `onEvent` callback fires for every event the server emits
   * (including `progress`, `complete`, and `error`), so the UI can render a
   * DatabaseUpdater-style live log + percent bar instead of a one-line
   * toast. `failed` is forwarded so partial-failure runs are visible.
   */
  async syncFields(
    fieldIds: string[] | undefined,
    onEvent?: (evt: {
      type: 'progress' | 'complete' | 'error';
      current?: number;
      total?: number;
      updated?: number;
      failed?: number;
      message?: string;
    }) => void
  ): Promise<{ updated: number; total: number; failed: number; message?: string }> {
    const res = await apiFetch('/api/custom-fields/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fieldIds: fieldIds && fieldIds.length > 0 ? fieldIds : undefined }),
    });
    if (!res.ok || !res.body) {
      const detail = await res.json().catch(() => ({}));
      throw new Error((detail as { error?: string }).error || 'Failed to start sync');
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let final: { updated: number; total: number; failed: number; message?: string } = {
      updated: 0,
      total: 0,
      failed: 0,
    };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let evt: {
          type?: string;
          current?: number;
          total?: number;
          updated?: number;
          failed?: number;
          message?: string;
          error?: string;
        };
        try {
          evt = JSON.parse(line.slice(6));
        } catch {
          continue;
        }
        if (evt.type === 'progress' || evt.type === 'complete' || evt.type === 'error') {
          onEvent?.({
            type: evt.type,
            current: evt.current,
            total: evt.total,
            updated: evt.updated,
            failed: evt.failed,
            message: evt.message,
          });
        }
        if (evt.type === 'complete') {
          final = {
            updated: evt.updated ?? 0,
            total: evt.total ?? 0,
            failed: evt.failed ?? 0,
            message: evt.message,
          };
        }
        // Server promotes "all-failed" runs to `type: 'error'`. Older payload
        // shape used `evt.error`; honour both so the UI never gets a silent
        // "Done. Touched 0 task(s)." that masks a real failure.
        if (evt.type === 'error' || evt.error) {
          throw new Error(evt.message || evt.error || 'Custom-field sync failed');
        }
      }
    }
    return final;
  },
};

export default CustomFieldsService;
